import katex from 'katex';

/**
 * Rich-text helpers for question stems and options.
 *
 * Question content can be authored HTML (paragraphs, inline images), tables
 * (inline markdown pipes or a separate `tableContent` string), and LaTeX math
 * (delimiters \(...\) inline, \[...\] / $$...$$ display). Rendering any of it as
 * plain text leaks raw markup / pipes / "\frac{}{}" to students (ROO-118/120/121/125).
 *
 * `questionToHtml` returns the HTML to inject for a stem (or null when it's plain
 * text). `renderOption` does the same for an answer option.
 *
 * NOTE: single `$` is CURRENCY in our data ("$19.25"), never math — so we only
 * treat \(  \[  $$ as math delimiters, never a lone $.
 */

/** True when the string contains HTML tags worth rendering as markup. */
export function hasHtml(s: string | null | undefined): boolean {
  return !!s && /<[a-z][\s\S]*>/i.test(s);
}

/** True when the string contains LaTeX math delimiters (not a bare $). */
export function hasMath(s: string | null | undefined): boolean {
  return !!s && /\\\(|\\\[|\$\$/.test(s);
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (c) => map[c] || c);
}

function renderMathSegment(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(tex, { displayMode, throwOnError: false, output: 'html' });
  } catch {
    return escapeHtml(tex);
  }
}

// Math delimiters, in priority order (display first so $$/\[ win over \( ).
const MATH_RE = /\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|\\\(([\s\S]+?)\\\)/g;

/**
 * Render PLAIN text that may contain math: escape the prose, KaTeX-render the
 * math segments (raw, un-escaped), preserve line breaks. Use for plain-text
 * stems/options — never on already-HTML strings.
 */
function renderRichText(text: string): string {
  const parts: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  MATH_RE.lastIndex = 0;
  while ((m = MATH_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(escapeHtml(text.slice(last, m.index)).replace(/\n/g, '<br>'));
    const display = m[1] !== undefined || m[2] !== undefined;
    const tex = (m[1] ?? m[2] ?? m[3] ?? '').trim();
    parts.push(renderMathSegment(tex, display));
    last = MATH_RE.lastIndex;
  }
  if (last < text.length) parts.push(escapeHtml(text.slice(last)).replace(/\n/g, '<br>'));
  return parts.join('');
}

/** Replace math delimiters with KaTeX in an already-HTML string (no escaping). */
function renderMathInHtml(html: string): string {
  return html.replace(MATH_RE, (_full, disp1, disp2, inline) => {
    const display = disp1 !== undefined || disp2 !== undefined;
    return renderMathSegment((disp1 ?? disp2 ?? inline ?? '').trim(), display);
  });
}

/**
 * Strip script/style/iframe/object/embed blocks, inline event handlers and
 * javascript: URLs. Not a full sanitizer; preserves tables, images, style attrs.
 */
export function sanitizeHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|style|iframe|object|embed)\b[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '$1=$2#$2');
}

const TH_STYLE =
  'border:1px solid #ddd;padding:8px 12px;background-color:#f5f5f5;font-weight:bold;text-align:center;';
const TD_STYLE = 'border:1px solid #ddd;padding:8px 12px;text-align:center;';
const TABLE_STYLE = 'border-collapse:collapse;width:100%;margin:10px 0;';

function isSeparatorRow(line: string): boolean {
  return line.includes('-') && /^[\s|:-]+$/.test(line.trim());
}
function isTableRow(line: string): boolean {
  return line.includes('|') && line.trim().length > 0;
}

function rowsToTable(rows: string[]): string {
  const dataRows = rows.filter((r) => !isSeparatorRow(r));
  if (dataRows.length === 0) return '';
  let html = `<table style="${TABLE_STYLE}">`;
  dataRows.forEach((line, rowIndex) => {
    const cells = line.split('|').map((c) => c.trim()).filter((c) => c.length > 0);
    if (cells.length === 0) return;
    html += '<tr>';
    cells.forEach((cell) => {
      const tag = rowIndex === 0 ? 'th' : 'td';
      const style = rowIndex === 0 ? TH_STYLE : TD_STYLE;
      html += `<${tag} style="${style}">${escapeHtml(cell)}</${tag}>`;
    });
    html += '</tr>';
  });
  return html + '</table>';
}

export function tableContentToHtml(tableContent: string | null | undefined): string {
  if (!tableContent || typeof tableContent !== 'string') return '';
  const rows = tableContent.trim().split('\n').filter((l) => l.trim());
  return rows.length ? rowsToTable(rows) : '';
}

export function hasMarkdownTable(s: string | null | undefined): boolean {
  if (!s) return false;
  const rows = s.split('\n').filter((l) => isTableRow(l) && !isSeparatorRow(l));
  return rows.length >= 2;
}

function markdownToHtml(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (isTableRow(lines[i])) {
      const block: string[] = [];
      while (i < lines.length && isTableRow(lines[i])) { block.push(lines[i]); i++; }
      out.push(rowsToTable(block));
    } else {
      const l = lines[i].trim();
      if (l) out.push(`<p>${escapeHtml(l)}</p>`);
      i++;
    }
  }
  return out.join('');
}

/** Base HTML for a stem (HTML / markdown table / tableContent), or null for plain text. */
function buildBaseHtml(content: Record<string, unknown>, stem: string): string | null {
  const tableContent = typeof content.tableContent === 'string' ? content.tableContent : '';
  const wantsTable = !!content.hasTable && !!tableContent.trim();

  if (hasHtml(stem)) {
    let html = sanitizeHtml(stem);
    if (wantsTable && !/<table/i.test(html)) html += tableContentToHtml(tableContent);
    return html;
  }
  if (hasMarkdownTable(stem)) return markdownToHtml(stem);
  if (wantsTable) {
    const prose = stem.trim() ? `<p>${escapeHtml(stem.trim())}</p>` : '';
    return prose + tableContentToHtml(tableContent);
  }
  return null;
}

/**
 * HTML to render for a question stem, or null when it's plain text. Handles
 * authored HTML, tables, and LaTeX math (math rendered as a final pass).
 */
export function questionToHtml(
  content: Record<string, unknown> | undefined,
  stem: string,
): string | null {
  const base = buildBaseHtml(content || {}, stem);
  if (base != null) return renderMathInHtml(base);
  if (hasMath(stem)) return renderRichText(stem);
  return null;
}

/** HTML to render for an answer option, or null when it's plain text. */
export function renderOption(text: string | null | undefined): string | null {
  const t = text || '';
  if (hasMath(t)) return renderRichText(t);
  if (hasHtml(t)) return renderMathInHtml(sanitizeHtml(t));
  return null;
}
